import { DetailedHTMLProps, HTMLAttributes } from 'react';
import { GETMymeDetails } from '@myme/api';
import { UUID } from '@myme/utils';
import { CourseIcon, MymePreviewLocationBasedIcon, MymeTimeBasedIcon, PriceIcon } from 'src/icons';
import { usePalette, useTranslation } from 'src/hooks';
import { creatorProfilePageRoute, mymeDetailsPageRoute } from 'src/routes/route-list';
import { toast, ToastType } from 'src/utils';
import { FlexColumn } from 'src/components/layout';
import { Dialog } from 'src/components/organisms';
import { useGetMymeDetails } from '../pages/mymes/details/useGetMymeDetails';

interface Props {
  mymeId: UUID;
  onClose: () => void;
  myme?: GETMymeDetails;
}

const mymeBottomPanelStyle = {
  position: 'relative',
  zIndex: 10,
  width: '100%',
  gridArea: `bottomPanel`,
  display: 'grid',
  webkitAlignItems: 'center',
  webkitBoxAlign: 'center',
  msFlexAlign: 'center',
  alignItems: 'center',
  gridTemplateAreas: "'likes rating price visibility mymeType'",
  gridTemplateColumns: 'repeat(3, min-content) 1fr 2.5rem',
  marginTop: '1rem'
} as DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const chipStyle = {
  fontFamily: 'Poppins,sans-serif',
  display: 'inline-flex',
  alignItems: 'center',
  webkitBoxPack: 'center',
  justifyContent: 'center',
  color: '#FFFFFF',
  whiteSpace: 'nowrap',
  transition:
    'background-color 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
  cursor: 'default',
  outline: 0,
  textDecoration: 'none',
  border: 0,
  padding: 0,
  verticalAlign: 'middle',
  boxSizing: 'border-box',
  maxWidth: 'min-content',
  height: 'min-content',
  backgroundColor: '#00C4FF',
  borderRadius: ' 0.5rem',
  fontSize: '12px'
} as DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const chipLabelStyle = {
  padding: '3px 5px',
  textTransform: 'uppercase',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  paddingLeft: '12px',
  paddingRight: '12px',
  whiteSpace: 'nowrap'
} as DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const headerFlexStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  position: 'relative',
  zIndex: 10,
  gridArea: 'header'
} as DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const getHeaderStyle = (previewImageFilePath: string) =>
  ({
    position: 'relative',
    height: '19rem',
    width: '19rem',
    borderRadius: '0.75rem',
    background: `url(${previewImageFilePath})`,
    backgroundSize: 'cover',
    textAlign: 'left',

    display: 'grid',
    gridTemplateAreas: `'header' 'details' 'bottomPanel'`,
    gridTemplateRows: `min-content 1fr min-content`,
    gridTemplateColumns: '100%',
    padding: '0.5rem 1rem'
  } as DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>);

const detailsStyle = {
  position: 'relative',
  zIndex: 10,
  gridArea: 'details',
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'flex-end'
} as DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const linkStyle = {
  color: '#fff',
  textDecoration: 'none'
} as DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const descriptionStyle = {
  fontSize: '12px',
  maxHeight: '5.5rem',
  overflow: 'hidden',
  display: '-webkit-box',
  webkitLineClamp: '1',
  webkitBoxOrient: 'vertical'
} as DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const hiddenChipStyle = {
  maxWidth: '100%',
  fontFamily: 'Poppins,sans-serif',
  fontSize: '0.8125rem',
  display: 'inline-flex',
  webkitBoxAlign: 'center',
  alignItems: 'center',
  webkitBoxPack: 'center',
  justifyContent: 'center',
  color: 'rgba(0, 0, 0, 0.87)',
  borderRadius: '16px',
  whiteSpace: 'nowrap',
  transition:
    'background-color 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
  cursor: 'default',
  outline: 0,
  textDecoration: 'none',
  padding: 0,
  verticalAlign: 'middle',
  boxSizing: 'border-box',
  gridArea: 'visibility',
  height: '1.5rem',
  width: '4rem',
  backgroundColor: 'transparent',
  border: '1px solid #fff',
  marginLeft: 'auto'
} as DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const hiddenChipLabelStyle = {
  color: '#fff',
  padding: '0 0.5rem',
  fontSize: '12px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
} as DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const priceStyle = {
  gridArea: `price`,
  justifySelf: 'center',
  margin: '8px',
  display: 'flex'
} as DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const iconStyle = {
  userSelect: 'none',
  display: 'inline-block',
  fill: 'currentColor',
  flexShrink: 0,
  transition: 'fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
  marginRight: '6px',
  height: '12px',
  width: '12px'
} as DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const typeChipStyle = {
  gridArea: `mymeType`,
  justifySelf: 'flex-end',
  display: 'flex'
} as DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const typeIconContainerStyle = {
  position: 'relative',
  width: '1.75rem',
  height: '1.75rem',
  backgroundColor: '#fff',
  borderRadius: '50%'
} as DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const typeIconStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  userSelect: 'none',
  display: 'inline-block',
  fill: 'currentColor',
  flexShrink: 0,
  transition: 'fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
  height: '1.25rem',
  width: '1.25rem'
} as DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

type IconName = 'likes' | 'rating';

export const EmbedMymeDialog = ({ mymeId, onClose, myme: propMyme }: Props) => {
  const { t } = useTranslation();
  const { success } = usePalette();
  const { data: mymeDetails } = useGetMymeDetails(mymeId, !propMyme);

  const myme = propMyme || mymeDetails;

  const { type, likes, price, isHidden, rating, description } = myme || {};

  const copyEmbed = async () => {
    const embedMyme = document.getElementById('embedMyme');
    if (!embedMyme) {
      toast(ToastType.ERROR, t('pages.my_mymes.embed_myme_dialog.copy_error'));
      return;
    }
    await navigator.clipboard.writeText(embedMyme?.innerHTML);
    toast(ToastType.SUCCESS, t('pages.my_mymes.embed_myme_dialog.copy_success'));
  };

  const appURL = `${window.location.protocol}//${window.location.host}`;

  return (
    <Dialog
      title={t('pages.my_mymes.embed_myme_dialog.title')}
      onClose={onClose}
      defaultActionButtons={{
        action: copyEmbed,
        btnConfirmText: t('pages.my_mymes.embed_myme_dialog.button')
      }}
      enableBackdropClick
      enableEscapeKeyDown
    >
      <FlexColumn alignItems='center' textAlign='center'>
        {myme && (
          <div data-subreddit='reactjs' id='embedMyme'>
            <link rel='preconnect' href='https://fonts.googleapis.com' />
            <link rel='preconnect' href='https://fonts.gstatic.com' crossOrigin='' />
            <link href='https://fonts.googleapis.com/css2?family=Poppins:wght@300&display=swap' rel='stylesheet' />
            <style>
              {`@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300&display=swap');`}

              {`.myme:after {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              height: 100%;
              width: 100%;
              background: transparent
              linear-gradient(180deg, #00000000 0%, #00000000 20%, #000000C8 84%, #000000E6 100%)
              0% 0% no-repeat padding-box;
            }
            p {
                display: block;
                margin-block-start: 1em;
                margin-block-end: 1em;
                margin-inline-start: 0px;
                margin-inline-end: 0px;
                margin: 0;
                font-family: Poppins,sans-serif;
                font-weight: 400;
                line-height: 1.43;
                color: #FFFFFF;
                font-size: 12px;
            }
            h5{
              margin: 0;
              font-weight: 900;
              font-family: Poppins,sans-serif;
              font-size: 16px;
              line-height: 1.334;
              color: #FFFFFF;
            }
            .dotted-myme-name {
              font-size: 16px;
              max-width: 19rem;
              max-height: 5rem;
              overflow: hidden;
              display: -webkit-box;
              -webkit-line-clamp: 3;
              -webkit-box-orient: vertical;
            }
            `}
            </style>
            <div style={getHeaderStyle(myme?.previewImageFilePath)} className='myme'>
              <div style={headerFlexStyle}>
                <div>
                  <div style={chipStyle}>
                    <span style={chipLabelStyle}>{myme?.category}</span>
                  </div>
                </div>
              </div>
              <div style={detailsStyle}>
                <a
                  href={`${appURL}${mymeDetailsPageRoute(myme?.id)}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  style={linkStyle}
                >
                  <p className='dotted-myme-name'>{myme?.name}</p>
                </a>
                <a
                  href={`${appURL}${creatorProfilePageRoute(myme?.author.id)}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  style={linkStyle}
                >
                  <p style={{ fontSize: '14px' }}>{`${t('pages.feed.myme.author')}  ${myme?.author.name}`}</p>
                </a>
                <p style={descriptionStyle}>{description}</p>
              </div>
              <div style={mymeBottomPanelStyle}>
                <IconWithText
                  name='likes'
                  text={likes || 0}
                  icon={
                    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 17.549 15.355'>
                      <path
                        // eslint-disable-next-line max-len
                        d='M15.845,3.3a4.687,4.687,0,0,0-6.4.466l-.675.7-.675-.7A4.687,4.687,0,0,0,1.7,3.3a4.922,4.922,0,0,0-.339,7.126L8,17.271a1.075,1.075,0,0,0,1.553,0l6.632-6.848A4.918,4.918,0,0,0,15.845,3.3Z'
                        transform='translate(0.001 -2.248)'
                        fill='#FF5C60'
                      />
                    </svg>
                  }
                />
                <IconWithText
                  name='rating'
                  text={rating || '-'}
                  icon={
                    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 19.173 17.803'>
                      <path
                        // eslint-disable-next-line max-len
                        d='M20.695,9.538H14.4L12.487,3.829a.693.693,0,0,0-1.3,0L9.273,9.538H2.935a.687.687,0,0,0-.685.685.5.5,0,0,0,.013.116.658.658,0,0,0,.287.484l5.174,3.646L5.738,20.241a.687.687,0,0,0,.235.77.662.662,0,0,0,.385.167.839.839,0,0,0,.428-.154l5.05-3.6,5.05,3.6a.8.8,0,0,0,.428.154.615.615,0,0,0,.381-.167.678.678,0,0,0,.235-.77l-1.986-5.773,5.131-3.68.124-.107a.718.718,0,0,0,.223-.458A.725.725,0,0,0,20.695,9.538Z'
                        transform='translate(-2.25 -3.375)'
                        fill='#FFCC65'
                      />
                    </svg>
                  }
                />
                {!!price && (
                  <div style={priceStyle}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <svg style={iconStyle}>
                        <PriceIcon fill={success.main} />
                      </svg>
                      <p>{`${price.toFixed(2)}€`}</p>
                    </div>
                  </div>
                )}
                {isHidden && (
                  <div style={hiddenChipStyle}>
                    <span style={hiddenChipLabelStyle}>{t('pages.feed.myme.hidden')}</span>
                  </div>
                )}
                {type && (
                  <div style={typeChipStyle}>
                    <div style={typeIconContainerStyle}>
                      <svg style={typeIconStyle}>
                        {type === 'LOCATION_BASED' && <MymePreviewLocationBasedIcon />}
                        {type === 'TIME_BASED' && <MymeTimeBasedIcon fill='#000' />}
                        {type === 'COURSE' && <CourseIcon fill='#000' />}
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </FlexColumn>
    </Dialog>
  );
};

interface IconTextProps {
  name: IconName;
  icon: JSX.Element;
  text: number | string | undefined;
}

const iconContainerStyle = { display: 'flex', alignItems: 'center', margin: '8px' };

const IconWithText = ({ name, icon, text }: IconTextProps) => {
  return (
    <div
      style={{
        gridArea: `${name}`,
        justifySelf: 'center'
      }}
    >
      <div style={iconContainerStyle}>
        <svg style={iconStyle}>{icon}</svg>
        <p>{text}</p>
      </div>
    </div>
  );
};
